import { Model, Factory, belongsTo, Response, hasMany } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randNumber } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MirageServer, MockServer } from './server.js'
import { filterable, pageable, PaginationError } from './utils.js'
import type { StockMoveItemInputs } from '@/composables/stock.js'
type StockItem = SchemaAttributes<'StockItem'>

export const model = (Model as ModelDefinition<StockItem>).extend({
  department: belongsTo('department'),
  organizationItem: belongsTo('organizationItem'),
  batches: hasMany('batch'),
})

export const factory = Factory.extend<StockItem>({
  units() {
    return randNumber({ min: 1, max: 999999 }).toString()
  },
  packages() {
    return randNumber({ min: 1, max: 999999 }).toString()
  },

  afterCreate(stockItem, server) {
    if (!stockItem.department) {
      const department = server.schema.find(
        'department',
        stockItem.departmentId,
      )
      stockItem.update({ department })
    }
    if (!stockItem.organizationItem) {
      const organizationItem = server.schema.find(
        'organizationItem',
        stockItem.organizationItemId,
      )
      stockItem.update({ organizationItem })
    }
  },
})

export function routes(server: MockServer) {
  server.get('/stock/items', function (this: MirageServer, schema, request) {
    const stockItems = schema.all('stockItem')
    const filtered = filterable(stockItems, request, {
      'department.id': (item, value) => item.department?.id === value,
      'location.id': () => true,
      ids: (item, value) => !value || item?.organizationItem?.id === value,
      'item.ids': (item, value) =>
        !value || item?.organizationItem?.id === value,
    })
    try {
      const { data, meta } = pageable(filtered, request)
      const dataWithItem = data.models.map((stockItem) => {
        return {
          id: stockItem.id,
          attributes: {
            ...stockItem.attrs,
            item: stockItem.organizationItem,
            batches: stockItem.batches?.models ?? [],
          },
        }
      })

      return {
        ...this.serialize({ data: dataWithItem }),
        meta: {
          pagination: meta,
        },
      }
    } catch (e) {
      if (e instanceof PaginationError) {
        return new Response(404, {}, e.message)
      }
    }

    // Fallback for unexpected errors
    return new Response(500, {}, 'Internal Server Error')
  })

  server.post('/stock/move', (schema, request) => {
    // For different negative values of batch packages there are different errors are raised
    // It's raised for checking errors when frontend is mocked
    type BatchError = Record<
      'packages' | 'units' | 'non_field_errors',
      string[]
    >
    type BackendErrors = {
      non_field_errors?: string[]
      destination_department_id?: string[]
      destination_location_id?: string[]
      source_location_id?: string[]
      source_department_id?: string[]
      items: {
        non_field_errors?: string[]
        source_item_id?: string[]
        destination_item_id?: string[]
        batches?: BatchError[]
      }[]
    }

    const requestBody = JSON.parse(request.requestBody)
    const items: StockMoveItemInputs[] = requestBody.data.attributes.items
    const backendValidationErrors: BackendErrors = { items: [] }
    let locationError = false
    let errorField = ''

    // Helper function to handle batch errors based on packages and units
    const handleBatchErrors = (batch: Record<'packages' | 'units', number>) => {
      const batchError = {} as BatchError

      // Check for various package errors based on their values
      if (batch.packages < -20) {
        locationError = true
        return { field: 'department', error: {} }
      }

      if (batch.packages < -10) {
        return { field: 'item', error: {} }
      }

      if (batch.packages < -5) {
        batchError.non_field_errors = [
          'Level of batch at the selected location: 1.000',
        ]
      } else if (batch.packages < 0) {
        batchError.packages = [
          'Ensure this value is greater than or equal to 0.',
        ]
      }

      // Check for units errors
      if (batch.units < 0) {
        batchError.units = ['Ensure this value is greater than or equal to 0.']
      }
      if (Object.keys(batchError).length) {
        return {
          field: 'batch',
          error: batchError,
        }
      }
      return {}
    }
    // Iterate over items and handle errors for each batch
    items.forEach((item) => {
      const batches = item.batches ?? []
      const batchErrors: BatchError[] = []
      let itemErrors: BackendErrors['items'][number] = {}

      // Process each batch and accumulate errors
      batches.forEach((batch) => {
        const { field, error } = handleBatchErrors(batch)
        errorField = field || errorField
        batchErrors.push(error as BatchError)
      })

      // Add item-level errors based on error field
      if (errorField === 'item') {
        itemErrors = {
          non_field_errors: ['Packages or units required.'],
          source_item_id: ['This field is required.'],
          destination_item_id: ['This field is required.'],
        }
      } else if (errorField === 'batch') {
        itemErrors.batches = batchErrors
      }

      // If errors exist, push them to the backendValidationErrors object
      if (errorField) {
        backendValidationErrors.items?.push(itemErrors)
      }
    })

    // If there is a location error, add specific department/location-related errors
    if (locationError) {
      Object.assign(backendValidationErrors, {
        non_field_errors: ['Something is wrong.'],
        destination_department_id: [
          'Batches not enabled in destination department.',
        ],
        destination_location_id: ['Cannot move into same location.'],
        source_location_id: ['This field is required.'],
        source_department_id: ['This field is required.'],
      })
    }

    // Return appropriate HTTP response based on presence of errors
    return backendValidationErrors.items.length || locationError
      ? new Response(400, {}, { errors: [backendValidationErrors] })
      : new Response(204)
  })
}

export function seeds(server: MockServer) {
  void server.lazySeedingManager?.seedBasic('StockLocation')

  const batch1 = server.create('batch', {
    id: '1',
    batch_number: 'batch 1',
    units: '100',
    packages: '10',
  })
  const batch2 = server.create('batch', {
    id: '2',
    batch_number: 'batch 2',
    units: '20',
    packages: '2',
  })
  const batch3 = server.create('batch', {
    id: '3',
    batch_number: 'batch 3',
    units: '30',
    packages: '3',
  })
  const batch4 = server.create('batch', {
    id: '4',
    batch_number: 'batch 4',
    units: '30',
    packages: '3',
  })
  server.create('stockItem', {
    id: '1',
    packages: '12',
    units: '120',
    departmentId: '6',
    organizationItemId: '1',
    batches: [batch1, batch2],
  })
  server.create('stockItem', {
    id: '2',
    organizationItemId: '2',
    departmentId: '6',
    packages: '12',
    units: '120',
    batches: [batch3, batch4],
  })
  server.create('stockItem', {
    id: '3',
    organizationItemId: '3',
    departmentId: '6',
    packages: '12',
    units: '120',
  })
  server.create('stockItem', {
    id: '4',
    organizationItemId: '4',
    departmentId: '9',
    packages: '12',
    units: '100',
  })
  server.create('stockItem', {
    id: '5',
    organizationItemId: '5',
    departmentId: '9',
    packages: '10',
    units: '100',
  })
}
